import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { interpret, type Actor } from '../state-machine/interpret';
import type {
  EventObject,
  Implementations,
  State,
  StateMachine,
} from '../state-machine/types';
import { requiredImplementationNames } from '../state-machine/validate';

/**
 * Subscribe a React component to a state machine.
 *
 * The actor is created once and kept stable; `services` / `guards` / `inspect`
 * are read through a ref so the latest closures (e.g. React Query `mutateAsync`)
 * are used without recreating the actor. State is read via `useSyncExternalStore`
 * with the machine's initial snapshot as the server snapshot (no hydration flash).
 *
 * Wrapper keys come from the machine config (every `invoke.src` / guard name),
 * not from the impls object passed on first render — so an impl that is missing
 * when its invoke actually runs fails observably (routed to that state's
 * `onError`) rather than depending on which keys existed at mount.
 *
 * @returns `[state, send]` — `state.value` is the current phase.
 */
export function useStateMachine<C, V extends string, E extends EventObject>(
  machine: StateMachine<C, V>,
  impls: Implementations<C, E> = {}
): [State<C, V>, (event: E | E['type']) => void] {
  const implsRef = useRef(impls);
  // Synced in an effect (not during render) so a discarded concurrent render
  // can't leak its impls into the ref. Between a commit and this effect the ref
  // briefly holds the previous render's impls — fine here: services/guards only
  // run post-commit on events/settlements, and adjacent renders' closures are
  // equivalent for that purpose. `useRef(impls)` seeds the first render.
  useEffect(() => {
    implsRef.current = impls;
  });

  const actorRef = useRef<Actor<C, V, E> | null>(null);
  if (actorRef.current === null) {
    const required = requiredImplementationNames(machine);
    const services: NonNullable<Implementations<C, E>['services']> = {};
    for (const key of required.services) {
      services[key] = (context, meta) => {
        const impl = implsRef.current.services?.[key];
        if (!impl)
          throw new Error(`[@heidi/flow] Service "${key}" has no implementation.`);
        return impl(context, meta);
      };
    }
    const guards: NonNullable<Implementations<C, E>['guards']> = {};
    for (const key of required.guards) {
      guards[key] = (context, event) => {
        const impl = implsRef.current.guards?.[key];
        if (!impl) throw new Error(`[@heidi/flow] Guard "${key}" has no implementation.`);
        return impl(context, event);
      };
    }
    actorRef.current = interpret<C, V, E>(machine, {
      services,
      guards,
      input: impls.input,
      inspect: (record, context) => implsRef.current.inspect?.(record, context),
    });
  }
  const actor = actorRef.current;

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const unsubscribe = actor.subscribe(onStoreChange);
      actor.start();
      return () => {
        unsubscribe();
        actor.stop();
      };
    },
    [actor]
  );

  const state = useSyncExternalStore(
    subscribe,
    actor.getSnapshot,
    actor.getInitialSnapshot
  );

  const send = useCallback((event: E | E['type']) => actor.send(event), [actor]);

  return [state, send];
}
